import React, { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

export const WaSendBox = ({ groupKey, label, count, templates = [], disabled, onSent }) => {
  const [mode, setMode] = useState("template");
  const [message, setMessage] = useState("");
  const [tpl, setTpl] = useState("");
  const [nameParam, setNameParam] = useState(true);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!window.confirm(`Send this WhatsApp to all ${count} numbers in "${label}"? Gupshup charges per message.`)) { return; }
    setBusy(true);
    try {
      await api.post(`/wa-groups/${groupKey}/send`, mode === "template" ? { template_id: tpl, name_param: nameParam } : { message });
      toast.success("Sending started — see progress below");
      onSent();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Card className="glass border-gold/20 p-4 space-y-3" data-testid="wa-send-card">
      <p className="font-serif font-semibold text-gold">Send to {label}</p>
      <div className="flex gap-2">
        {[["template", "Approved template"], ["text", "Free text"]].map(([m, l]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`wa-mode-${m}`}
            className={`rounded-full px-3 py-1 text-xs border ${mode === m ? "bg-[#25D366] text-[#06140f] border-[#25D366]" : "border-gold/30 text-emerald-100/70"}`}>{l}</button>
        ))}
      </div>
      {mode === "template" ? (
        <>
          {templates.some((t) => t.status === "APPROVED") && (
            <select value={tpl} onChange={(e) => setTpl(e.target.value)} data-testid="wa-template-select" className="w-full h-10 rounded-md border border-border bg-card/60 px-2 text-sm text-white">
              <option value="">Pick an approved template…</option>
              {templates.filter((t) => t.status === "APPROVED").map((t) => <option key={t.id} value={t.id}>{t.name} ({t.language})</option>)}
            </select>
          )}
          {templates.find((t) => t.id === tpl) && <p className="text-xs text-emerald-100/70 whitespace-pre-wrap rounded-md bg-white/5 p-2" data-testid="wa-template-preview">{templates.find((t) => t.id === tpl).text}</p>}
          <Input value={tpl} onChange={(e) => setTpl(e.target.value)} placeholder="Template ID from Gupshup (Templates page)" data-testid="wa-template-id" className="bg-card/60" />
          <label className="flex items-center gap-2 text-xs text-emerald-100/70">
            <input type="checkbox" checked={nameParam} onChange={(e) => setNameParam(e.target.checked)} data-testid="wa-name-param" /> Put each person's name in {"{{1}}"}
          </label>
          <p className="text-[11px] text-emerald-100/50">Use this for everyone — templates reach people even if they haven't messaged you.</p>
        </>
      ) : (
        <>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Assalamu Alaikum {name}, …" data-testid="wa-message" className="bg-card/60" />
          <p className="text-[11px] text-amber-200/80">Free text only reaches people who messaged your WhatsApp in the last 24 hours. Write {"{name}"} to insert each person's name.</p>
        </>
      )}
      <Button onClick={send} disabled={disabled || busy || !count || (mode === "template" ? !tpl.trim() : !message.trim())} data-testid="wa-send-btn" className="w-full bg-[#25D366] text-[#06140f] hover:bg-[#25D366]/90 gap-2 font-semibold">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {count} numbers
      </Button>
    </Card>
  );
};
